import {axiosInstance} from '.';
import {store} from 'src/app/store';
import CryptoJs from 'crypto-js';

export const cryptoKey = process.env.REACT_APP_PASS_PHRASE ?? '';

type ConfigType = Parameters<
    NonNullable<Parameters<typeof axiosInstance.interceptors.request.use>[0]>
>[0];
type ErrorType = Parameters<
    NonNullable<Parameters<typeof axiosInstance.interceptors.request.use>[1]>
>[0];

/**
 * The stored token, ready to be sent as a header.
 *
 * Exported because the websocket needs the same value: its CONNECT frame carries the token now, and
 * two readers of one encrypted blob is one more chance for them to disagree about where it lives.
 */
export const storedAuthorization = (): string | null => {
    const auth = localStorage.getItem('auth');
    if (!auth) {
        return null;
    }
    try {
        const data = JSON.parse(
            CryptoJs.AES.decrypt(auth, cryptoKey.toString()).toString(CryptoJs.enc.Utf8)
        );
        return data.authorization ?? null;
    } catch {
        // A blob written by an older key, or half-written. Treated as no token: the connection then
        // stays anonymous and is refused a personal topic, which is the safe reading.
        return null;
    }
};

/**
 * The stored refresh token - the name of the session on the server.
 *
 * Signing out needs it: clearing this browser's storage only made the browser forget the session,
 * which stayed open until it aged out. Same blob, same reasons as {@link storedAuthorization}.
 */
export const storedRefreshToken = (): string | null => {
    const auth = localStorage.getItem('auth');
    if (!auth) {
        return null;
    }
    try {
        const data = JSON.parse(
            CryptoJs.AES.decrypt(auth, cryptoKey.toString()).toString(CryptoJs.enc.Utf8)
        );
        return data.refreshToken ?? null;
    } catch {
        return null;
    }
};

export const authHeadersFromLocalStorage = async (config: ConfigType) => {
    // The language the reader is in, so text the server holds comes back in it. Permission
    // descriptions live in the catalogue rather than in lang/*.json, because a business can add a
    // permission and would otherwise wait for a frontend release to find out what it does. A header
    // rather than a parameter: it is a property of the request, not of any one question, and Spring
    // resolves it into the locale context without a signature having to carry it.
    const lang = store.getState().common.lang;
    if (config.headers && lang) {
        config.headers['Accept-Language'] = lang;
    }

    if (!localStorage.getItem('auth')) {
        return config;
    }
    const authorization = storedAuthorization();
    if (config.headers && authorization) {
        config.headers.authorization = authorization;
    }

    // The client used to inject businessId and realEstateId into the body of every request for
    // real-estate users - a data slice decided on this side and merely checked on the other. The
    // server now derives it from the caller's role binding and overwrites whatever arrives, so
    // sending it was at best redundant and at worst a second, disagreeing answer that only showed up
    // in the mismatch log.

    return config;
};

export const rejectError = (error: ErrorType) => Promise.reject(error);
